// Key Encapsulation Mechanism suites for establishing shared secrets between
// peers without prior key exchange. Used for session key negotiation in the
// mesh layer. Provides classical (X25519-HKDF / RFC 9180 DHKEM), post-quantum
// (ML-KEM-768 / FIPS 203), and a hybrid that combines both via HKDF.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { x25519 } from '@noble/curves/ed25519.js';
import { ml_kem768 } from '@noble/post-quantum/ml-kem.js';
import { hkdfSha256 } from './hkdf.js';
const DHKEM_LABEL = new TextEncoder().encode('hhs3-dhkem-x25519');
const HYBRID_LABEL = new TextEncoder().encode('hhs3-hybrid-kem-x25519-mlkem768');
function concatBytes(...arrays) {
    const totalLen = arrays.reduce((sum, a) => sum + a.length, 0);
    const result = new Uint8Array(totalLen);
    let offset = 0;
    for (const a of arrays) {
        result.set(a, offset);
        offset += a.length;
    }
    return result;
}
/**
 * DHKEM construction with X25519 + HKDF-SHA256 (RFC 9180 style).
 * Encapsulate generates an ephemeral X25519 keypair, computes a DH shared
 * secret with the recipient's public key, and derives the output via HKDF.
 * The ciphertext is the ephemeral public key.
 */
export const x25519Hkdf = {
    name: 'x25519-hkdf',
    publicKeySize: 32,
    secretKeySize: 32,
    ciphertextSize: 32,
    sharedSecretSize: 32,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            const secretKey = x25519.utils.randomSecretKey();
            const publicKey = x25519.getPublicKey(secretKey);
            return { publicKey, secretKey };
        });
    },
    encapsulate(publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const ephemeral = x25519.utils.randomSecretKey();
            const ephemeralPk = x25519.getPublicKey(ephemeral);
            const dh = x25519.getSharedSecret(ephemeral, publicKey);
            const kemContext = concatBytes(ephemeralPk, publicKey);
            const sharedSecret = hkdfSha256.deriveKey(dh, kemContext, DHKEM_LABEL, 32);
            return { ciphertext: ephemeralPk, sharedSecret };
        });
    },
    decapsulate(ciphertext, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const publicKey = x25519.getPublicKey(secretKey);
            const dh = x25519.getSharedSecret(secretKey, ciphertext);
            const kemContext = concatBytes(ciphertext, publicKey);
            return hkdfSha256.deriveKey(dh, kemContext, DHKEM_LABEL, 32);
        });
    }
};
export const mlKem768 = {
    name: 'ml-kem-768',
    publicKeySize: 1184,
    secretKeySize: 2400,
    ciphertextSize: 1088,
    sharedSecretSize: 32,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            return ml_kem768.keygen();
        });
    },
    encapsulate(publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const { cipherText, sharedSecret } = ml_kem768.encapsulate(publicKey);
            return { ciphertext: cipherText, sharedSecret };
        });
    },
    decapsulate(ciphertext, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            return ml_kem768.decapsulate(ciphertext, secretKey);
        });
    }
};
/**
 * Hybrid KEM: X25519-HKDF + ML-KEM-768.
 * Both KEMs are run independently. Ciphertexts are concatenated.
 * Shared secrets are combined via HKDF with a domain separation label.
 */
export const x25519Hkdf_mlKem768 = {
    name: 'x25519-hkdf+ml-kem-768',
    publicKeySize: x25519Hkdf.publicKeySize + mlKem768.publicKeySize,
    secretKeySize: x25519Hkdf.secretKeySize + mlKem768.secretKeySize,
    ciphertextSize: x25519Hkdf.ciphertextSize + mlKem768.ciphertextSize,
    sharedSecretSize: 32,
    generateKeyPair() {
        return __awaiter(this, void 0, void 0, function* () {
            const classical = yield x25519Hkdf.generateKeyPair();
            const pq = yield mlKem768.generateKeyPair();
            return {
                publicKey: concatBytes(classical.publicKey, pq.publicKey),
                secretKey: concatBytes(classical.secretKey, pq.secretKey),
            };
        });
    },
    encapsulate(publicKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const classicalPk = publicKey.subarray(0, x25519Hkdf.publicKeySize);
            const pqPk = publicKey.subarray(x25519Hkdf.publicKeySize);
            const classical = yield x25519Hkdf.encapsulate(classicalPk);
            const pq = yield mlKem768.encapsulate(pqPk);
            const combinedSs = concatBytes(classical.sharedSecret, pq.sharedSecret);
            const sharedSecret = hkdfSha256.deriveKey(combinedSs, new Uint8Array(0), HYBRID_LABEL, 32);
            return {
                ciphertext: concatBytes(classical.ciphertext, pq.ciphertext),
                sharedSecret
            };
        });
    },
    decapsulate(ciphertext, secretKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const classicalCt = ciphertext.subarray(0, x25519Hkdf.ciphertextSize);
            const pqCt = ciphertext.subarray(x25519Hkdf.ciphertextSize);
            const classicalSk = secretKey.subarray(0, x25519Hkdf.secretKeySize);
            const pqSk = secretKey.subarray(x25519Hkdf.secretKeySize);
            const classicalSs = yield x25519Hkdf.decapsulate(classicalCt, classicalSk);
            const pqSs = yield mlKem768.decapsulate(pqCt, pqSk);
            const combinedSs = concatBytes(classicalSs, pqSs);
            return hkdfSha256.deriveKey(combinedSs, new Uint8Array(0), HYBRID_LABEL, 32);
        });
    }
};
